import { createUUID } from '../../../utils'
import { PaletteNodes } from './palette-nodes'

export class NodeFactory {
  /**
   * Creates a node object from a raw node.
   * @param {WorkflowNode} node - The raw node object.
   * @returns {WorkflowNode} The formatted node object.
   */
  static fromRawNode(node) {
    return {
      ...node,
      type: node.type,
      subtype: node?.subtype || '',
      item_type: node?.item_type || '',
      node_uid: node.node_uid,
      label: node.data.label || node.ui.label || '',
      icon: node.ui.icon || '',
      position: node.ui.position || { x: 0, y: 0 },
      metadata: node.ui?.metadata || {},
      available_connections: node?.available_connections || node?.ui?.available_connections || [],
      connection_limits: node?.connection_limits || [],
      data: {
        ...node.data,
        label: node.ui.label || node.data.label,
        description: node.data.description || node.ui?.metadata?.description || ''
      },
      ui: {
        ...node.ui,
        position: node.ui.position || NodeFactory.getDefaultPosition()
      },
      chip: node.chip
    }
  }

  /**
   * Creates a new node object.
   * @param {{
   *   type: string;
   *   subtype: string;
   *   item_type: string;
   *   icon: string;
   *   label: string;
   *   data: WorkflowNodeData;
   *   node_uid?: string;
   *   position: DiagramPosition;
   *   ui: WorkflowNodeUI;
   *   paletteItem?: PaletteNode;
   *   metadata: Record<string, any>;
   *   available_connections: Array<string>;
   *   connection_limits: Record<string, any>;
   *   data: WorkflowNodeData;
   *   node_uid: string;
   *   chip: Number;
   * }} params - The parameters for creating the node.
   * @returns {WorkflowNode} The created node object.
   */
  static createNode(params) {
    const {
      type,
      subtype,
      item_type,
      label,
      icon,
      position,
      metadata,
      available_connections,
      connection_limits,
      data,
      node_uid = createUUID(),
      ui,
      chip,
      paletteItem
    } = params

    return {
      type,
      subtype,
      item_type,
      node_uid,
      metadata: metadata || {},
      position,
      available_connections:
        available_connections || paletteItem?.available_connections || ui?.available_connections || [],
      connection_limits: connection_limits || paletteItem?.connection_limits || ui?.connection_limits || [],
      data: { ...data, label },
      ui: {
        ...ui,
        position,
        icon
      },
      chip
    }
  }

  /**
   * Creates a canvas node object from a node.
   * @param {WorkflowNode} node - The node object.
   * @returns {CanvasWorkflowNode} The canvas node object.
   */
  static createCanvasNode(node) {
    if (!node || typeof node !== 'object' || !node.node_uid) {
      console.warn('Invalid node object for canvas node creation:', node)
      return null
    }

    const defaultValidation = {
      isValid: false,
      errors: []
    }

    // If no icon is present, try to find it from the palette template or node properties
    let customIcon = node.ui.icon || ''
    if (!customIcon && node.type) {
      // Find the palette template for this node type and subtype
      for (const section of PaletteNodes) {
        if (section.items) {
          const template = section.items.find(
            item => item.type === node.type && (!item.subtype || item.subtype === node.subtype)
          )
          if (template && template.icon) {
            customIcon = template.icon
            break
          }
        }
      }
    }

    return {
      id: node.node_uid,
      label: node.data.label || node.ui.label,
      description: node.data.description || node.ui?.metadata?.description || '',
      position: node.ui.position,
      hasInboundPort: node.type !== 'start', // get that from node template
      hasOutboundPort: node.type !== 'stop' && node.type !== 'end', // get that from node template
      icon: customIcon,
      type: node.type,
      subtype: node?.subtype || '',
      item_type: node?.item_type || '',
      validation: node?.ui?.validation || defaultValidation,
      metadata: node?.ui?.metadata || {},
      available_connections: node?.available_connections || node?.ui?.available_connections || [],
      connection_limits: node?.connection_limits || {},
      chip: node.chip
    }
  }

  /**
   * Gets the default position for a node.
   * @returns {DiagramPosition} The default position object.
   */
  static getDefaultPosition = () => ({ x: 0, y: 0 })

  /**
   * Updates node description
   * @param {WorkflowNode} node - The node to update
   * @param {string} description - The new description
   * @returns {WorkflowNode} The updated node
   */
  static updateNodeDescription(node, description) {
    return {
      ...node,
      data: {
        ...node.data,
        description
      }
    }
  }

  /**
   * Gets node description
   * @param {WorkflowNode} node - The node
   * @returns {string} The node description
   */
  static getNodeDescription(node) {
    return node?.data?.description || node?.ui?.metadata?.description || ''
  }
}
